import { Component, HostListener, Input } from '@angular/core';
import { ChartOptions } from 'chart.js';
import { ActivatedRoute } from '@angular/router';
import { BaseListCtl, ServiceLocatorService } from '@ncs/ncs-lib';

@Component({
  selector: 'app-dynamic-chart',
  templateUrl: './dynamic-chart.component.html',
  styleUrls: ['./dynamic-chart.component.scss'],
})
export class DynamicChartComponent extends BaseListCtl {
  @Input() attributes: any;
  @Input() data: any;
  @Input() collection_name: any;

  constructor(
    public override locator: ServiceLocatorService,
    public override route: ActivatedRoute
  ) {
    super('', locator, route);
    let _self = this;
    locator.config.getProperty('endpoint.chart').then((property: string) => {
      this.initApiEndpoint(property);
    });
  }

  chartContainerWidth: string = '100%'; // Default width
  cardBodyHeight: string = 'auto'; // Default height

  @HostListener('window:resize', ['$event'])
  onResize(event: Event | null): void {
    const windowWidth = event
      ? (event.target as Window).innerWidth
      : window.innerWidth;

    if (windowWidth < 576) {
      this.chartContainerWidth = '90%'; // Small screens
    } else if (windowWidth < 768) {
      this.chartContainerWidth = '70%'; // Medium screens
    } else {
      this.chartContainerWidth = '70%'; // Larger screens
    }

    const windowHeight = event
      ? (event.target as Window).innerHeight
      : window.innerHeight;

    const headerHeight = 170; // Approximate height of header/form in px
    const footerHeight = 60; // Approximate height of footer (if any)
    const buffer = 50; // Optional extra buffer

    // Calculate the available height for the card-body
    const availableHeight = windowHeight - headerHeight - footerHeight - buffer;

    this.cardBodyHeight = `${availableHeight}px`; // Set height dynamically
  }

  override ngOnInit(): void {
    this.onResize(null); // Initialize size based on the current window width
  }

  // Chart options
  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
    },
    scales: {
      x: {
        display: true,
      },
      y: {
        display: true,
        beginAtZero: true,
      },
    },
  };

  public chartdata: any;

  public filterAttributes(): any {
    const allowedTypes = [
      'text',
      'number',
      'object',
      'email',
      'url',
      'map',
      'list',
    ];
    return this.attributes.filter((e: any) => allowedTypes.includes(e['type']));
  }

  public drawChart() {
    let _self = this;
    let code = this.form.data.code;
    let fields = this.attributes.filter((e: any) => e.code === code);
    let attr_code = this.collection_name + '.' + code;

    if (
      fields[0].type === 'text' ||
      fields[0].type === 'number' ||
      fields[0].type === 'email' ||
      fields[0].type === 'list'
    ) {
      let url = _self.api.endpoint + '/value/' + attr_code;
      _self.locator.http.post(url, {}, function (res: any, error: boolean) {
        if (error) {
          alert('Server Error:' + error);
          return;
        }
        let data = res.result['data'];
        let labels = Object.keys(data);
        let chartData = Object.values(data);
        const backgroundColors = _self.generateColors(chartData.length);
        if (fields[0].type === 'number' && _self.form.data.type !== 'range') {
          alert('Value work for text');
          _self.chartdata = {};
          return;
        }

        if (
          (fields[0].type === 'text' ||
            fields[0].type === 'email' ||
            fields[0].type === 'list') &&
          _self.form.data.type === 'range'
        ) {
          alert('Range work for numbers');
          _self.chartdata = {};
          return;
        }

        _self.chartdata = {
          labels: labels,
          datasets: [
            {
              label: fields[0]['label'],
              data: chartData,
              backgroundColor: backgroundColors,
            },
          ],
        };

        if (_self.form.data.type == 'range') {
          _self.chartdata = _self.getNumericData(fields[0], _self.chartdata);
        }

        if (_self.form.data.showPercentage) {
          _self.chartdata.datasets[0].data = _self.convertToPercentages(
            _self.chartdata.datasets[0].data
          );
          _self.updateChartOptions(_self.form.data.showPercentage);
        }
      });
    } else {
      console.log('data not availeble');
    }
  }

  getMaxValue(field: any, data: any): number {
    let labels = data.labels;
    return labels.length > 0 ? Math.max(...labels) : 0;
  }

  private getNumericData(field: any, data: any): any {
    let _self = this;
    let max_values = this.getMaxValue(field, data);

    const rangeSize = max_values / 4;
    const range1 = { min: 0, max: rangeSize }; // 0% - 25%
    const range2 = { min: rangeSize, max: 2 * rangeSize }; // 25% - 50%
    const range3 = { min: 2 * rangeSize, max: 3 * rangeSize }; // 50% - 75%
    const range4 = { min: 3 * rangeSize, max: max_values }; // 75% - 100%

    let countRange1 = 0,
      countRange2 = 0,
      countRange3 = 0,
      countRange4 = 0;
    let chartDatatemp = data.datasets[0].data;

    let labels_temp = data.labels;

    labels_temp.forEach((value: any, index: number) => {
      if (value !== undefined && value !== null) {
        if (value >= range1.min && value <= range1.max) {
          countRange1 = countRange1 + chartDatatemp[index];
        } else if (value > range2.min && value <= range2.max) {
          countRange2 = countRange2 + chartDatatemp[index];
        } else if (value > range3.min && value <= range3.max) {
          countRange3 = countRange3 + chartDatatemp[index];
        } else if (value > range4.min && value <= range4.max) {
          countRange4 = countRange4 + chartDatatemp[index];
        }
      }
    });

    let labels = [
      `${range1.min} - ${range1.max}`,
      `${range2.min} - ${range2.max}`,
      `${range3.min} - ${range3.max}`,
      `${range4.min} - ${range4.max}`,
    ];

    const totalCount = countRange1 + countRange2 + countRange3 + countRange4;

    let chartData = [countRange1, countRange2, countRange3, countRange4];
    data.labels = labels;
    data.datasets[0].data = chartData;
    const backgroundColors = _self.generateColors(chartData.length);
    data.datasets[0].backgroundColors = backgroundColors;
    return data;
  }

  private rawDataCache: number[] = [];
  private convertToPercentages(data: number[]): number[] {
    const total = data.reduce((sum, value) => sum + value, 0);
    this.rawDataCache = [...data];
    return total > 0 ? data.map((value) => (value / total) * 100) : data;
  }

  private updateChartOptions(showPercentage: boolean) {
    let _self = this;
    this.chartOptions = {
      ...this.chartOptions,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function (value) {
              return showPercentage ? value + '%' : value;
            },
          },
        },
      },
      plugins: {
        ...this.chartOptions.plugins,
        tooltip: {
          callbacks: {
            label: function (tooltipItem) {
              const dataset = tooltipItem.dataset;
              const dataIndex = tooltipItem.dataIndex;
              const label = dataset.label || '';
              const value = dataset.data[dataIndex];
              const total = dataset.data.reduce((a: any, b: any) => a + b, 0);
              const percentage = ((value as number) / total) * 100;
              const rawValue = _self.rawDataCache[dataIndex];
              return showPercentage
                ? `${label}: ${rawValue} (${percentage.toFixed(2)}%)`
                : `${label}: ${rawValue}`;
            },
          },
        },
      },
    };
  }

  // Helper function to generate random colors

  private colorPalette: string[] = [
    'rgba(45, 49, 66, 0.8)', // Dark blue-grey
    'rgba(75, 75, 109, 0.8)', // Dark indigo
    'rgba(29, 53, 87, 0.8)', // Dark navy
    'rgba(69, 123, 157, 0.8)', // Deep teal
    'rgba(230, 57, 70, 0.8)', // Dark red
    'rgba(168, 218, 220, 0.8)', // Muted teal
    'rgba(29, 105, 122, 0.8)', // Dark cyan
    'rgba(120, 66, 18, 0.8)', // Dark brown
    'rgba(50, 50, 50, 0.8)', // Dark grey
    'rgba(34, 40, 49, 0.8)', // Almost black
    'rgba(123, 31, 162, 0.8)', // Dark purple
    'rgba(67, 160, 71, 0.8)', // Dark green
    'rgba(255, 87, 34, 0.8)', // Dark orange
    'rgba(244, 81, 30, 0.8)', // Deep burnt orange
    'rgba(103, 58, 183, 0.8)', // Dark violet
  ];

  private generateColors(count: number): string[] {
    const colors = [];
    for (let i = 0; i < count; i++) {
      // Add color from palette if within range, otherwise generate a random color
      if (i < this.colorPalette.length) {
        colors.push(this.colorPalette[i]);
      } else {
        // If colorPalette is exhausted, generate random colors
        const color = `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
          Math.random() * 255
        )}, ${Math.floor(Math.random() * 255)}, 0.7)`;
        colors.push(color);
      }
    }
    return colors;
  }
}
